import { hashSync as bcryptHashSync } from "bcryptjs";

/**
 * "Server relief" password scheme (PASSWORD_MODE=relief, the default).
 *
 * Why: Cloudflare Workers Free allows only 10ms of CPU per request, while
 * bcrypt at cost >= 10 needs 60-100ms — a server-side bcrypt login would be
 * killed with Error 1102 on the free plan. Instead:
 *
 *   1. The browser deterministically derives proof = bcrypt(password,
 *      salt-from-email, cost=10). bcrypt runs on the client, where there is
 *      no CPU budget.
 *   2. The Worker stores / compares only sha256(proof) ("sr1$<hex>") —
 *      ~0.05ms of CPU.
 *
 * Security: a stolen D1 database yields sha256(bcrypt(password)) verifiers.
 * Logging in requires the bcrypt proof, so an attacker must still brute
 * force bcrypt at cost 10 per guess — the outer SHA-256 adds no shortcut
 * because its input domain is bcrypt outputs, not passwords.
 *
 * The email-derived salt is deterministic by design so the browser can
 * re-derive the same proof at every login without a server round trip.
 * Consequence: emails are immutable (enforced by the API).
 */

const SALT_NAMESPACE = "themislex.bcrypt.v1:";

/** sha256 hex digest via WebCrypto (works in Workers, browsers and Node 18+). */
export async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Derive the bcrypt proof for a password, as the login/setup screens do. */
export async function derivePasswordProof(
  password: string,
  email: string,
  cost = 10,
): Promise<string> {
  const emailNorm = email.trim().toLowerCase();
  const digest = await sha256Hex(SALT_NAMESPACE + emailNorm);
  const salt = `$2a$${String(cost).padStart(2, "0")}$${digest.slice(0, 22)}`;
  return bcryptHashSync(password, salt);
}

/** Server-side verifier string stored in users.password_hash (relief mode). */
export async function verifierForProof(proof: string): Promise<string> {
  return `sr1$${await sha256Hex(proof)}`;
}

/** Constant-time-ish string comparison (defence in depth). */
export function safeEqualHex(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}
